'use client';

import { useEffect, useState } from 'react';
import {
  dismissUpdate,
  fetchDeployedVersion,
  getRunningVersion,
  isNewerDeployment,
  reloadToVersion,
  stripVersionQuery,
  wasUpdateDismissed,
} from '@/lib/frontend-version';

/**
 * Non-blocking notice. It never reloads during route startup; Reload is explicit.
 * A failed version fetch leaves the dashboard alone.
 */
export function FrontendUpdateNotice() {
  const [deployed, setDeployed] = useState<string | null>(null);

  useEffect(() => {
    stripVersionQuery();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetchDeployedVersion().then((version) => {
        if (cancelled || !version) return;
        if (!isNewerDeployment(getRunningVersion(), version) || wasUpdateDismissed(version)) return;
        setDeployed(version);
      });
    }, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  if (!deployed) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 70,
        width: 'min(520px, calc(100% - 32px))',
        borderRadius: 12,
        border: '1px solid rgba(20,30,25,0.12)',
        background: '#FFFFFF',
        color: '#1B2A24',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        padding: 16,
      }}
    >
      <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>A new version was released</p>
      <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.5, color: '#3B4A43' }}>
        Reload to fetch the latest files.
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={() => reloadToVersion(deployed)}
          style={{
            minHeight: 40,
            border: 0,
            borderRadius: 10,
            background: '#1F3B2E',
            color: '#FFFFFF',
            padding: '8px 14px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => {
            dismissUpdate(deployed);
            setDeployed(null);
          }}
          style={{
            minHeight: 40,
            borderRadius: 10,
            border: '1px solid #D6D0C0',
            background: '#FFFFFF',
            color: '#1B2A24',
            padding: '8px 14px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
